import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Api from "../../services/Api";
import PageHeader from "../../components/PageHeader";
import PolicyUploader from "./PolicyUploader";
import { Policy } from "@/types/policy";
import AppDialog from "../../components/Dialog";
import { downloadFile, removePolicy } from "../../services/privacyPolicy";
import Confirm from "../../components/Dialog/Confirm";
import dayjs from "dayjs";

const PrivacyPolicy = ({ apiLink }: { apiLink: string }) => {
  const [uploaderView, setUploaderView] = useState<"add" | "edit" | null>(null);
  const [isViewerVisible, setIsViewerVisible] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<number>();
  const [pdfFile, setPdfFile] = useState<string>();
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const { data, isLoading, isError, refetch: refetchPolicies } = useQuery("privacyPolicies", () =>
    Api.getTableData(apiLink, localStorage.getItem("DP_PROJECT_ID"))
  );
  const { mutateAsync } = useMutation("removePolicy", removePolicy);
  const {
    data: file,
    isLoading: fetchingFile,
    isError: FileError,
    refetch,
  } = useQuery(
    "privacyPolicyDownload",
    () => downloadFile(selectedPolicy?.id!),
    { enabled: !!selectedPolicy?.id }
  );

  useEffect(() => {
    if (selectedPolicy?.id) {
      // getTableData()
      refetch().then((res) => {        
        if (res.data?.data) {
          // External link
          setIsViewerVisible(false);
          window.open(res.data.data.privacy_policy_url, "_blank");
        } else {
          // const data = await downloadFile(selectedPolicy?.id);
          const imgUrl = URL.createObjectURL(res.data);
          setPdfFile(imgUrl);
          if (res.data.type !== "application/pdf") {
            setIsViewerVisible(false); // File will be downloaded if not pdf
          }
        }
      });
    }
  }, [selectedPolicy]);

  const previewFile = (policy: Policy) => {
    setSelectedPolicy(policy);
    setIsViewerVisible(true);
  };

  const deletePolicy = () => {
    mutateAsync(policyToDelete!).then(() => refetchPolicies())
  };

  return (
    <div>
      <PageHeader
        title="Privacy Policy"
        buttonLabel="Import Documents"
        onButtonClick={() => setUploaderView("add")}
      />
      {uploaderView !== null && (
        <PolicyUploader
          mode={uploaderView}
          selectedPolicy={selectedPolicy}
          onClose={() => setUploaderView(null)}
          refetch={refetchPolicies}
        />
      )}
      <div className="grid grid-cols-12 gap-6">
        {data?.data?.data?.results?.map((policy: Policy) => (
          <div
            key={policy.id}
            role="button"
            onClick={() => {
              previewFile(policy);
            }}
            className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
          >
            <figure className="bg-white">
              <img
                style={{ height: '50px', width: 'auto', border: 'none', marginLeft: '20px' }}
                src={policy.project.logo_url}
                alt=""
                className="w-full border"
              />
              <figcaption className="text-left p-4">
                <div className="flex items-center justify-between">
                  <h4 className="mb-3">{policy.privacy_policy_text}</h4>
                  <div className="flex items-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPolicy(policy);
                        setUploaderView("edit");
                      }}
                    >
                      <PencilIcon className="h-4 w-4 mr-2 text-purple-1 hover:text-purple-800" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPolicyToDelete(policy.id);
                      }}
                    >
                      <TrashIcon className="h-4 w-4 text-red-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
                <h5 className="text-grey-1 mb-3">
                  Version - {policy.privacy_policy_version}
                </h5>
                <h5 className="text-grey-1">
                  Last Updated - {dayjs(policy.privacy_policy_date).format('MMM DD, YYYY')}
                </h5>
              </figcaption>
            </figure>
          </div>
        ))}
      </div>
      <AppDialog onClose={setIsViewerVisible} isOpen={isViewerVisible}>
        {selectedPolicy && pdfFile && (
          <iframe
            src={`${pdfFile}`}
            title="displayFile"
            width="100%"
            height="99%"
            style={{ borderStyle: "solid", minHeight: "80vh" }}
          />
        )}
      </AppDialog>
      <Confirm
        title="Delete Policy?"
        description="Are you sure you want to delete this policy?"
        isOpen={Boolean(policyToDelete)}
        onClose={() => setPolicyToDelete(undefined)}
        onProceed={deletePolicy}
      />
    </div>
  );
};

export default PrivacyPolicy;

const MyNoRenderer = ({ document, fileName }: any) => {
  const fileText = fileName || document?.fileType || "";

  if (fileText) {
    return <div>No Renderer Error! ({fileText})</div>;
  }

  return <div>No Renderer Error!</div>;
};
